import {
  cancelledServicesColumns,
  cancelledServicesDefaultColumns,
  cancelledServicesSorts,
  cancelledServicesSpecificFilters,
} from '@cabgo/shared';
import type { ReportViewDefinition } from '../reportViewTypes';
export const cancelledServicesView: ReportViewDefinition = {
  key: 'cancelled_services',
  titleKey: 'cancelledServices.title',
  descriptionKey: 'cancelledServices.description',
  dateBasisKey: 'cancelledServices.dateBasis',
  columns: cancelledServicesColumns,
  defaultColumns: cancelledServicesDefaultColumns,
  dateMode: 'range',
  supportsSearch: true,
  defaultSort: 'cancelledAt',
  sorts: cancelledServicesSorts,
  filters: cancelledServicesSpecificFilters.map((filter) => ({
    ...filter,
    labelKey: `cancelledServices.filter.${filter.id}`,
    ...(filter.values
      ? {
          options: filter.values.map((value) => ({
            value,
            labelKey: `cancelledServices.option.${filter.id}.${value}`,
          })),
        }
      : {}),
  })),
  coverage: [{ field: 'missingEventCount', labelKey: 'cancelledServices.coverage.missingEventCount' }],
  valueLabels: {
    cancel_actor_type: {
      PASSENGER: 'cancelledServices.option.actorType.PASSENGER',
      AGENT: 'cancelledServices.option.actorType.AGENT',
      ORG_USER: 'cancelledServices.option.actorType.ORG_USER',
      PARTNER: 'cancelledServices.option.actorType.PARTNER',
      SYSTEM: 'cancelledServices.option.actorType.SYSTEM',
      UNKNOWN: 'cancelledServices.option.actorType.UNKNOWN',
    },
    payment_state: {
      FREE: 'cancelledServices.option.paymentState.FREE',
      FINANCE_REVIEW: 'cancelledServices.option.paymentState.FINANCE_REVIEW',
      NOT_BILLABLE: 'cancelledServices.option.paymentState.NOT_BILLABLE',
      UNBILLED: 'cancelledServices.option.paymentState.UNBILLED',
      OUTSTANDING: 'cancelledServices.option.paymentState.OUTSTANDING',
      PARTIAL: 'cancelledServices.option.paymentState.PARTIAL',
      SETTLED: 'cancelledServices.option.paymentState.SETTLED',
    },
    service_mode: {
      RIDE_AND_CARE: 'cancelledServices.option.serviceMode.RIDE_AND_CARE',
      CARE: 'cancelledServices.option.serviceMode.CARE',
      RIDE: 'cancelledServices.option.serviceMode.RIDE',
      TRAVEL: 'cancelledServices.option.serviceMode.TRAVEL',
    },
    trip_status: { CANCELLED: 'cancelledServices.value.CANCELLED' },
    trip_leg: { OUTBOUND: 'cancelledServices.value.OUTBOUND', RETURN: 'cancelledServices.value.RETURN' },
    dispatch_mode: { AUTO: 'cancelledServices.value.AUTO', MANUAL: 'cancelledServices.value.MANUAL' },
    service_date_basis: {
      SCHEDULED_START: 'cancelledServices.value.SCHEDULED_START',
      CREATED_AT_FALLBACK: 'cancelledServices.value.CREATED_AT_FALLBACK',
    },
    date_basis: {
      CANCELLATION_EVENT: 'cancelledServices.value.CANCELLATION_EVENT',
      UNAVAILABLE: 'cancelledServices.value.UNAVAILABLE',
    },
    cancel_reason_label_source: { CURRENT_REASON: 'cancelledServices.value.CURRENT_REASON' },
    scope_provenance: { CURRENT_SERVICE_GROUP: 'cancelledServices.value.CURRENT_SERVICE_GROUP' },
    fare_state: {
      NONE: 'cancelledServices.value.NONE',
      PROVISIONAL: 'cancelledServices.value.PROVISIONAL',
      FINAL: 'cancelledServices.value.FINAL',
      CANCELLED_SNAPSHOT: 'cancelledServices.value.CANCELLED_SNAPSHOT',
    },
    quote_provenance: {
      BOOKING_SNAPSHOT: 'cancelledServices.value.BOOKING_SNAPSHOT',
      UNAVAILABLE: 'cancelledServices.value.UNAVAILABLE',
    },
    billing_distance_source: {
      STATUS_EVENTS: 'cancelledServices.value.STATUS_EVENTS',
      GPS_TRACE: 'cancelledServices.value.GPS_TRACE',
      UNKNOWN: 'cancelledServices.value.UNKNOWN',
      PLANNED_FALLBACK: 'cancelledServices.value.PLANNED_FALLBACK',
    },
    billing_duration_source: {
      STATUS_EVENTS: 'cancelledServices.value.STATUS_EVENTS',
      GPS_TRACE: 'cancelledServices.value.GPS_TRACE',
      UNKNOWN: 'cancelledServices.value.UNKNOWN',
      PLANNED_FALLBACK: 'cancelledServices.value.PLANNED_FALLBACK',
    },
    base_payment_status: {
      PENDING_SLIP: 'cancelledServices.value.PENDING_SLIP',
      SLIP_SUBMITTED: 'cancelledServices.value.SLIP_SUBMITTED',
      VERIFIED: 'cancelledServices.value.VERIFIED',
      REJECTED: 'cancelledServices.value.REJECTED',
      WAIVED: 'cancelledServices.value.WAIVED',
      REFUND_PENDING: 'cancelledServices.value.REFUND_PENDING',
      REFUNDED: 'cancelledServices.value.REFUNDED',
    },
    overage_payment_status: {
      PENDING_SLIP: 'cancelledServices.value.PENDING_SLIP',
      SLIP_SUBMITTED: 'cancelledServices.value.SLIP_SUBMITTED',
      VERIFIED: 'cancelledServices.value.VERIFIED',
      REJECTED: 'cancelledServices.value.REJECTED',
      WAIVED: 'cancelledServices.value.WAIVED',
      REFUND_PENDING: 'cancelledServices.value.REFUND_PENDING',
      REFUNDED: 'cancelledServices.value.REFUNDED',
    },
    currency: { THB: 'cancelledServices.value.THB' },
  },
  summary: [
    { field: 'cancelledTripCount', labelKey: 'cancelledServices.summary.cancelledTripCount', unit: 'count' },
    { field: 'fareTotalSum', labelKey: 'cancelledServices.summary.fareTotalSum', unit: 'money' },
    { field: 'billedTotalSum', labelKey: 'cancelledServices.summary.billedTotalSum', unit: 'money' },
    { field: 'receivedTotalSum', labelKey: 'cancelledServices.summary.receivedTotalSum', unit: 'money' },
    {
      field: 'outstandingTotalSum',
      labelKey: 'cancelledServices.summary.outstandingTotalSum',
      unit: 'money',
    },
    { field: 'freeTripCount', labelKey: 'cancelledServices.summary.freeTripCount', unit: 'count' },
    { field: 'financeReviewCount', labelKey: 'cancelledServices.summary.financeReviewCount', unit: 'count' },
    { field: 'missingFareCount', labelKey: 'cancelledServices.summary.missingFareCount', unit: 'count' },
    {
      field: 'passengerActorCount',
      labelKey: 'cancelledServices.summary.passengerActorCount',
      unit: 'count',
    },
    { field: 'agentActorCount', labelKey: 'cancelledServices.summary.agentActorCount', unit: 'count' },
    { field: 'orgUserActorCount', labelKey: 'cancelledServices.summary.orgUserActorCount', unit: 'count' },
    { field: 'partnerActorCount', labelKey: 'cancelledServices.summary.partnerActorCount', unit: 'count' },
    { field: 'systemActorCount', labelKey: 'cancelledServices.summary.systemActorCount', unit: 'count' },
    { field: 'unknownActorCount', labelKey: 'cancelledServices.summary.unknownActorCount', unit: 'count' },
  ],
  primarySummary: [
    'cancelledTripCount',
    'fareTotalSum',
    'billedTotalSum',
    'receivedTotalSum',
    'outstandingTotalSum',
  ],
  summaryGroups: [
    { labelKey: 'summaryGroupOverview', fields: ['cancelledTripCount'] },
    {
      labelKey: 'summaryGroupFinance',
      fields: [
        'fareTotalSum',
        'billedTotalSum',
        'receivedTotalSum',
        'outstandingTotalSum',
        'freeTripCount',
        'financeReviewCount',
      ],
    },
    {
      labelKey: 'summaryGroupCancellation',
      fields: [
        'passengerActorCount',
        'agentActorCount',
        'orgUserActorCount',
        'partnerActorCount',
        'systemActorCount',
      ],
    },
    { labelKey: 'summaryGroupEvidence', fields: ['missingFareCount', 'unknownActorCount'] },
  ],
};
